import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { millisToMinutesAndSecondsString } from '../helpers/dateTimes';
import {
  getFromLocalStorage,
  StorageKey,
  setOnLocalStorage,
} from '../helpers/localstorage';
import { useTimer } from './useTimer';

const totalTimerDuration = 5 * 60 * 1000;

export const useResendEmailTimer = (resendFulfilledTimeStamp?: number) => {
  const [timerDuration, setTimerDuration] = useState<number | undefined>(
    undefined
  );
  const [isResendActive, setIsResendActive] = useState(true);
  const [remainingTime, setRemainingTime] = useState<string>('5:00');
  const lastResendRequest = useRef(resendFulfilledTimeStamp);

  const updateRemainingTime = useCallback((remainingTime: number) => {
    if (remainingTime > 0) {
      setRemainingTime(millisToMinutesAndSecondsString(remainingTime));
    } else {
      setTimerDuration(0);
      setIsResendActive(true);
      setRemainingTime(millisToMinutesAndSecondsString(totalTimerDuration));
    }
  }, []);

  const { start, stopAndClear } = useTimer(timerDuration ?? 0, {
    onDown: updateRemainingTime,
  });

  useEffect(() => stopAndClear, [stopAndClear]);

  useEffect(() => {
    const endTimerDateTimeString = getFromLocalStorage(
      StorageKey.ConfirmCodeTimerEndDate
    );

    const endTimerDateTime = dayjs(endTimerDateTimeString);
    const diffFromNow = endTimerDateTime.diff(dayjs(), 'milliseconds');
    if (diffFromNow > 0) {
      setRemainingTime(millisToMinutesAndSecondsString(diffFromNow));
      setTimerDuration(diffFromNow);
    }
  }, []);

  useEffect(() => {
    if (resendFulfilledTimeStamp !== lastResendRequest.current) {
      const endTimerDate = dayjs()
        .add(totalTimerDuration, 'milliseconds')
        .format('YYYY-MM-DDTHH:mm:ss');
      setOnLocalStorage(endTimerDate, StorageKey.ConfirmCodeTimerEndDate);
      setTimerDuration(totalTimerDuration);
    }
  }, [resendFulfilledTimeStamp]);

  useEffect(() => {
    if (timerDuration && timerDuration > 0) {
      stopAndClear();
      setIsResendActive(false);
      start();
    }
  }, [start, stopAndClear, timerDuration]);

  return {
    isResendActive,
    remainingTime,
  };
};
